import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import './ProductSearchModal.css'; // Vytvoříme i CSS soubor pro stylování

// Debounce hook, abychom nevolali API po každém stisku klávesy
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}

const ProductSearchModal = ({ isOpen, onClose, onProductSelect }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const debouncedSearchTerm = useDebounce(searchTerm, 300); // 300ms prodleva

    const searchProducts = useCallback(async (term) => {
        if (term.length < 2) {
            setResults([]);
            return;
        }
        setIsLoading(true);
        try {
            const response = await fetch('/server/quote_service/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'search_products', searchTerm: term })
            });
            if (!response.ok) {
                throw new Error('Search request failed');
            }
            const data = await response.json();
            setResults(data);
        } catch (error) {
            console.error("Error searching products:", error);
            toast.error("Failed to search for products.");
            setResults([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        searchProducts(debouncedSearchTerm);
    }, [debouncedSearchTerm, searchProducts]);

    if (!isOpen) {
        return null;
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Search for a Product in CRM</h2>
                    <button onClick={onClose} className="close-button">×</button>
                </div>
                <div className="modal-body">
                    <input
                        type="text"
                        placeholder="Type Product Name or Code..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="search-input"
                        autoFocus
                    />
                    <div className="results-container">
                        {isLoading && <div className="loading-indicator">Searching...</div>}
                        {!isLoading && results.length === 0 && debouncedSearchTerm.length > 1 && (
                            <div className="no-results">No products found.</div>
                        )}
                        {!isLoading && results.map(product => (
                            <div 
                                key={product.id} 
                                className="result-item"
                                onClick={() => onProductSelect(product)}
                            >
                                <div className="product-name">{product.Product_Name}</div>
                                <div className="product-code">{product.Product_Code}</div>
                                <div className="product-price">
                                    {new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'EUR' }).format(product.Unit_Price || 0)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductSearchModal;